/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Drawer from "@mui/material/Drawer";
import { styled } from "@mui/material/styles";

export default styled(Drawer)(({ theme, ownerState }) => {
  const { palette, boxShadows, transitions, breakpoints, functions } = theme;
  const { transparentSidenav, whiteSidenav, miniSidenav } = ownerState;

  const sidebarWidth = 250;
  const { transparent, white } = palette;
  const { xxl } = boxShadows;
  const { pxToRem, linearGradient } = functions;

  const darkBlue = linearGradient("#14325a", "#071526");
  let backgroundValue = darkBlue;

  if (transparentSidenav) {
    backgroundValue = transparent.main;
  } else if (whiteSidenav) {
    backgroundValue = white.main;
  }

  // styles for the sidenav when miniSidenav={false}
  const drawerOpenStyles = () => ({
    background: backgroundValue,
    transform: "translateX(0)",
    transition: transitions.create("transform", {
      easing: transitions.easing.sharp,
      duration: transitions.duration.shorter,
    }),

    [breakpoints.up("xl")]: {
      boxShadow: transparentSidenav ? "none" : xxl,
      marginBottom: transparentSidenav ? 0 : "inherit",
      left: "0",
      width: sidebarWidth,
      transform: "translateX(0)",
      transition: transitions.create(["width", "background-color"], {
        easing: transitions.easing.sharp,
        duration: transitions.duration.enteringScreen,
      }),
    },
  });

  // styles for the sidenav when miniSidenav={true}
  const drawerCloseStyles = () => ({
    background: backgroundValue,
    transform: `translateX(${pxToRem(-320)})`,
    transition: transitions.create("transform", {
      easing: transitions.easing.sharp,
      duration: transitions.duration.shorter,
    }),

    [breakpoints.up("xl")]: {
      boxShadow: transparentSidenav ? "none" : xxl,
      marginBottom: transparentSidenav ? 0 : "inherit",
      left: "0",
      width: pxToRem(96),
      overflowX: "hidden",
      transform: "translateX(0)",
      transition: transitions.create(["width", "background-color"], {
        easing: transitions.easing.sharp,
        duration: transitions.duration.shorter,
      }),
    },
  });

  return {
    "& .MuiDrawer-paper": {
      boxShadow: xxl,
      border: "none",
      height: "100vh",
      maxHeight: "100vh",
      "@supports (height: 100dvh)": {
        height: "100dvh",
        maxHeight: "100dvh",
      },
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      minHeight: 0,
      ...(miniSidenav ? drawerCloseStyles() : drawerOpenStyles()),
      // Mobile / tablet: full-screen menu that fades and drops in from the header.
      [breakpoints.down("xl")]: {
        width: "100vw",
        left: 0,
        right: 0,
        margin: 0,
        borderRadius: 0,
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        boxShadow: "none",
        opacity: miniSidenav ? 0 : 1,
        visibility: miniSidenav ? "hidden" : "visible",
        pointerEvents: miniSidenav ? "none" : "auto",
        transform: miniSidenav ? `translateY(${pxToRem(-12)})` : "translateY(0)",
        transition: miniSidenav
          ? "opacity 0.2s ease, transform 0.2s ease, visibility 0s linear 0.2s"
          : "opacity 0.24s ease, transform 0.24s cubic-bezier(0.2, 0.8, 0.2, 1), visibility 0s",
      },
    },
  };
});
